<template>
  <div class="app-nav-root">
    <header class="app-nav" :class="{ 'nav-scrolled': isScrolled }">
      <v-container>
        <div class="nav-content">
          <div class="nav-brand" @click="navigateTo('/')">
            <div class="nav-logo-icon">
              <div class="nav-hex"></div>
            </div>
            <div class="nav-logo-text">
              <div class="nav-wrenzo">Wrenzo</div>
              <div class="nav-suite">SUITE</div>
            </div>
          </div>

          <nav class="nav-links">
            <router-link to="/" class="nav-link" exact>Home</router-link>
            <router-link to="/why" class="nav-link">Why Wrenzo Suite</router-link>
            <router-link to="/features" class="nav-link">Features</router-link>
            <router-link to="/about" class="nav-link">About</router-link>
            <router-link to="/blog" class="nav-link">Blog</router-link>
          </nav>

          <div class="nav-actions">
            <v-btn outlined class="nav-btn-login" to="/login">
              Login
            </v-btn>
            <v-btn depressed class="nav-btn-demo" to="/demo">
              Request Demo
            </v-btn>
          </div>

          <button class="nav-burger" @click="drawer = true">
            <v-icon color="white">mdi-menu</v-icon>
          </button>
        </div>
      </v-container>
    </header>

    <!-- Mobile Drawer — deliberately a SIBLING of <header>, not nested inside
         it. .app-nav uses backdrop-filter for its frosted-glass effect, and
         backdrop-filter (like filter/transform) creates a new containing
         block for any position:fixed descendants — so if this drawer lived
         inside <header>, its "fixed" positioning would be scoped to the
         header's own ~70px height instead of the full viewport, clipping
         the overlay and nav links off-screen. Keeping it as a sibling avoids
         that entirely. -->
    <div v-if="drawer" class="drawer-overlay" @click="drawer = false">
      <div class="drawer-panel" @click.stop>
        <div class="drawer-header">
          <div class="drawer-logo">
            <div class="drawer-hex"></div>
          </div>
          <div class="drawer-brand">
            <div>Wrenzo</div>
            <div class="brand-sub">SUITE</div>
          </div>
          <button class="drawer-close" @click="drawer = false">
            <v-icon>mdi-close</v-icon>
          </button>
        </div>
        <v-divider class="my-4"></v-divider>
        <div class="drawer-nav">
          <a class="drawer-link" @click="navigateTo('/')">
            <v-icon left>mdi-home</v-icon> Home
          </a>
          <a class="drawer-link" @click="navigateTo('/why')">
            <v-icon left>mdi-star-four-points</v-icon> Why Wrenzo Suite
          </a>
          <a class="drawer-link" @click="navigateTo('/features')">
            <v-icon left>mdi-view-grid</v-icon> Features
          </a>
          <a class="drawer-link" @click="navigateTo('/about')">
            <v-icon left>mdi-information</v-icon> About
          </a>
          <a class="drawer-link" @click="navigateTo('/blog')">
            <v-icon left>mdi-post</v-icon> Blog
          </a>
          <v-divider class="my-4"></v-divider>
          <a class="drawer-link" @click="navigateTo('/login')">
            <v-icon left>mdi-login</v-icon> Login
          </a>
          <a class="drawer-link drawer-link-crimson" @click="navigateTo('/demo')">
            <v-icon left>mdi-calendar-check</v-icon> Request Demo
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AppNavigation',
  data() {
    return {
      drawer: false,
      isScrolled: false
    };
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    handleScroll() {
      this.isScrolled = window.scrollY > 20;
    },
    navigateTo(route) {
      this.drawer = false;
      this.$router.push(route);
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;600;700;800&display=swap');

.app-nav-root {
  display: contents;
}

.app-nav {
  --border-subtle: rgba(255, 255, 255, 0.09);
  --text-primary: #F5F6F7;
  --text-secondary: rgba(245, 246, 247, 0.68);
  --crimson: #D6293E;
  --crimson-light: #FF6B7A;
  --crimson-dark: #A91324;

  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 999;
  background: rgba(10, 11, 14, 0.88);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--border-subtle);
  font-family: 'Manrope', -apple-system, BlinkMacSystemFont, sans-serif;
  transition: background 0.3s ease, box-shadow 0.3s ease;
}

.app-nav.nav-scrolled {
  background: rgba(6, 7, 9, 0.96);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35);
}

.app-nav ::v-deep .v-btn {
  text-transform: none !important;
}

.nav-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 0;
}

.nav-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.nav-brand:hover {
  transform: scale(1.03);
}

.nav-logo-icon {
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.nav-hex {
  width: 26px;
  height: 26px;
  background: linear-gradient(150deg, var(--crimson-light) 0%, var(--crimson) 55%, var(--crimson-dark) 100%);
  clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
  animation: nav-hexspin 8s linear infinite;
}

@keyframes nav-hexspin {
  0%   { clip-path: polygon(50% 0%,  100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%); }
  50%  { clip-path: polygon(50% 5%,  95%  28%, 95%  72%, 50% 95%,  5% 72%, 5% 28%); }
  100% { clip-path: polygon(50% 0%,  100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%); }
}

.nav-logo-text {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.nav-wrenzo {
  font-family: 'Manrope', sans-serif;
  font-size: 1.2rem;
  font-weight: 800;
  color: var(--text-primary);
  letter-spacing: -0.01em;
}

.nav-suite {
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 2.5px;
  color: var(--crimson-light);
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 6px;
}

.nav-link {
  padding: 10px 16px;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  border-radius: 8px;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.nav-link:hover,
.nav-link.router-link-active {
  color: var(--crimson-light);
  background: rgba(214, 41, 62, 0.1);
}

.nav-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.nav-btn-login {
  border: 1.5px solid var(--crimson) !important;
  color: var(--crimson-light) !important;
  font-weight: 600 !important;
  border-radius: 9px !important;
}

.nav-btn-demo {
  background: linear-gradient(150deg, var(--crimson-light) 0%, var(--crimson) 60%, var(--crimson-dark) 100%) !important;
  color: #FFFFFF !important;
  font-weight: 700 !important;
  border-radius: 9px !important;
}

.nav-burger {
  display: none;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  cursor: pointer;
}

/* ==================== MOBILE DRAWER ==================== */
.drawer-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(10px);
  z-index: 1000;
}

.drawer-panel {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: 300px;
  background: #0E1013;
  border-right: 1px solid rgba(255, 255, 255, 0.09);
  box-shadow: 0 0 60px rgba(0, 0, 0, 0.5);
  z-index: 1001;
  overflow-y: auto;
  padding: 8px;
  font-family: 'Manrope', sans-serif;
}

.drawer-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 24px 16px 0;
}

.drawer-logo {
  width: 46px;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.drawer-hex {
  width: 30px;
  height: 30px;
  background: linear-gradient(150deg, #FF6B7A 0%, #D6293E 55%, #A91324 100%);
  clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
}

.drawer-brand {
  flex: 1;
  line-height: 1.2;
}

.drawer-brand > div:first-child {
  font-size: 18px;
  font-weight: 800;
  color: #F5F6F7;
}

.brand-sub {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 2px;
  color: #FF6B7A;
}

.drawer-close {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 8px;
  color: rgba(245, 246, 247, 0.68);
  cursor: pointer;
}

.drawer-close ::v-deep .v-icon {
  color: currentColor !important;
}

.drawer-nav {
  padding: 8px 16px 16px;
}

.drawer-link {
  display: flex;
  align-items: center;
  padding: 14px 16px;
  color: rgba(245, 246, 247, 0.68);
  font-size: 16px;
  font-weight: 600;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 4px;
}

.drawer-link ::v-deep .v-icon {
  color: currentColor !important;
}

.drawer-link:hover {
  background: rgba(214, 41, 62, 0.12);
  color: #FF6B7A;
  transform: translateX(8px);
}

.drawer-link-crimson {
  color: #FF6B7A;
}

@media (max-width: 960px) {
  .nav-links,
  .nav-actions {
    display: none;
  }

  .nav-burger {
    display: flex;
  }
}
</style>